import React, { useEffect } from "react";
import Header from "../sharedComponents/Header";
import projectsData from "./Project";
import { useParams } from "react-router-dom";
import Footer from "../sharedComponents/footer";
import { Helmet } from "react-helmet-async";
import NotFoundPage from "../subComponents/NotFoundPage";
import AOS from "aos";
import "aos/dist/aos.css";
import { motion } from "framer-motion";

const parseSolutionLines = (solution = "") =>
  solution
    .split(/\n|·|¶/)
    .map((s) => s.trim())
    .filter((s) => s.length > 10)
    .map((line) => {
      const colonIdx = line.indexOf(":");
      if (colonIdx > 0 && colonIdx < 60) {
        return {
          title: line.slice(0, colonIdx).trim(),
          body: line.slice(colonIdx + 1).trim(),
        };
      }
      return { title: null, body: line };
    });

const splitChallenge = (description = "") => {
  const colonIdx = description.indexOf(":");
  if (colonIdx > 0 && colonIdx < 60) {
    return {
      title: description.slice(0, colonIdx).trim(),
      body: description.slice(colonIdx + 1).trim(),
    };
  }
  return { title: null, body: description };
};
const CaseStudyPages = () => {
  const { id } = useParams();
  const project = projectsData.find((p) => p.id === id);

  useEffect(() => {
    AOS.init({ duration: 500, easing: "ease", once: true });
  }, []);

  if (!project) return <NotFoundPage />;

  const solutionLines = parseSolutionLines(project.solution);

  return (
    <>
      <Helmet>
        <title>{project?.metaTitle}</title>
        <meta name="description" content={project?.metaDescription} />
        <link rel="canonical" href={project.canonicalTag} />
      </Helmet>
      <Header />

      {/* ════════════════════════════════════
                HERO
            ════════════════════════════════════ */}
      <div className="overflow-hidden font-inter">
        <div className="relative w-full h-[39rem] flex items-center overflow-hidden">
          {/* Background Image */}
          <img
            src="https://webkorps-app.s3.us-east-1.amazonaws.com/real-success-proven-results-explore-our-case-studies.jpg"
            alt="case study"
            className="absolute inset-0 w-full h-[39rem] object-cover"
          />

          {/* Overlay */}
          <div className="absolute inset-0 bg-black/40"></div>

          {/* Content */}
          <div className="relative z-10 flex flex-col xl:flex-row items-center justify-between gap-10 px-[5%] xl:px-[50px] w-full">
            {/* LEFT CONTENT */}
            <div className="w-full xl:w-[55%] text-center xl:text-left">
              <h1
                className="text-[1.8rem] xl:text-[3.2rem] leading-[2.4rem] xl:leading-[3.4rem] text-white font-bold"
                data-aos="fade-left"
                dangerouslySetInnerHTML={{ __html: project.title }}
              />
               <h3 className="text-[1rem] xl:text-[1.3rem] ipad-pro:text-[1.35rem] md:text-[1.5rem]  text-white font-semibold  mt-[1.2rem] w-[95%]" data-aos="fade-left">{project.subTitle}</h3>
            </div>

            {/* RIGHT IMAGE */}
            <div className="w-full xl:w-[40%] flex justify-center xl:h-[70%]">
              <div className="rounded-lg shadow-lg overflow-hidden">
                <img
                  src={project.image}
                  alt={project.altText}
                  className={`h-full object-contain ${project.id === "paypal" ? "md:w-[60%]" : "md:w-[100%]"}`}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════
                STATS — dynamic from project.stats
            ════════════════════════════════════ */}
  {project.stats && project.stats.length > 0 && (
  <div className="py-[2.5rem] bg-[#F5F7FA] font-inter">
    <div className="mx-[7%] md:mx-[7%] xl:mx-[50px] ipad-pro:mx-[50px]">
      <div className="bg-white border border-gray-200 rounded-[20px] px-[3%] xl:px-[40px] py-[2.5rem] shadow-md">
        <div className="flex flex-col md:flex-row items-stretch">
          {project.stats.map((stat, index) => (
            <React.Fragment key={index}>
              
              <div className="flex-1 flex flex-col items-center justify-center px-6 text-center py-4 md:py-0">
                
                {/* VALUE */}
                <span className="text-[1.5625rem] xl:text-[2.1875rem] ipad-pro:text-[1.875rem] md:text-[2.1875rem] font-bold text-[#061C3D]">
                  {stat.value}
                </span>

                {/* LABEL */}
                <p className="text-gray-500 text-[0.9375rem] xl:text-[1rem] mt-2 leading-[1.5rem] min-h-[3rem] flex items-center justify-center">
                  {stat.label}
                </p>

              </div>

              {index < project.stats.length - 1 && (
                <div className="hidden md:block w-[2px] bg-gray-300 mx-2"></div>
              )}

            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  </div>
)}

      {/* ════════════════════════════════════
                ABOUT THE CLIENT
            ════════════════════════════════════ */}
      <div className="py-[4rem] bg-white font-inter">
        <div className="mx-[7%] md:mx-[7%] xl:mx-[50px] ipad-pro:mx-[50px]">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="flex flex-col overflow-hidden md:flex-col ipad-pro:flex-row xl:flex-row items-center xl:items-stretch px-[3%] xl:px-[30px] ipad-pro:px-[30px] justify-center text-center lg:text-left shadow-custom-lg rounded-[20px] bg-white gap-[60px] p-12"
          >
            <div className="flex flex-col w-full">
              <h2
                className="font-inter text-[1.5625rem] xl:text-[2.1875rem] ipad-pro:text-[1.875rem] md:text-[2.1875rem] leading-[2.45rem] font-bold text-center xl:text-left ipad-pro:text-left text-[#061C3D] w-full"
                data-aos="fade-right"
              >
                About the Client
              </h2>
              <p
                className="text-gray-700 mt-[2rem] text-[0.9375rem] xl:text-[1.15rem] ipad-pro:text-[1.125rem] md:text-[1.15rem] leading-[1.5875rem] font-inter"
                data-aos="fade-right"
              >
                {project.description}
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* ════════════════════════════════════
                CHALLENGES FACED
            ════════════════════════════════════ */}
      <div className="py-[4rem] bg-[#061C3D] font-inter">
        <div className="mx-[7%] md:mx-[7%] xl:mx-[50px] ipad-pro:mx-[50px]">
          <div className="flex flex-col overflow-hidden px-[3%] xl:px-[30px] ipad-pro:px-[30px] shadow-[0px_0px_3px_#707070] rounded-[20px] border-black border-[0.0001px] p-12">
            {/* Heading */}
            <div className="mb-8 w-full" data-aos="fade-up">
              <div
                className="flex flex-col items-center md:items-start text-white w-full"
                data-aos="flip-left"
              >
                <h2 className="text-[1.5625rem] xl:text-[2.1875rem] ipad-pro:text-[1.875rem] md:text-[2.1875rem] leading-[2.45rem] font-bold text-white mb-4">
                 The Problem & Challenges
                </h2>
                {project.challengeIntro && (
                  <p className="text-[0.9375rem] xl:text-[1.15rem] ipad-pro:text-[1.125rem] md:text-[1.15rem] leading-[1.5875rem] text-white/80 pt-[1.5rem]">
                    {project.challengeIntro}
                  </p>
                )}
              </div>
            </div>

            {/* Grid */}
            <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-6">
           {project.challenges.map((challenge, index) => {
  const { title, body } = splitChallenge(challenge.description);

  const isOdd = project.challenges.length % 2 !== 0;
  const isLast = index === project.challenges.length - 1;

  return (
    <motion.div
      key={index}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      viewport={{ once: true }}
      className={`bg-white/10 backdrop-blur-md border border-white/20 rounded-[20px] p-6 hover:bg-white/20 transition-all duration-300 h-full
        ${isOdd && isLast ? "md:col-span-2 md:max-w-[50%] md:mx-auto" : ""}
      `}
    >
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 min-w-[44px] rounded-full bg-[#1887C9] flex items-center justify-center text-white font-semibold text-[0.95rem] shadow-md flex-shrink-0">
                        {String(index + 1).padStart(2, "0")}
                      </div>
                      <div>
                        <h3 className="text-white font-bold text-[1rem] mb-1">
                          {title || `Challenge ${index + 1}`}
                        </h3>
                        <p className="text-white/70 text-[0.9375rem] xl:text-[1rem] leading-[1.6rem]">
                          {body}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════
                OUR SOLUTION
            ════════════════════════════════════ */}
      <div className="py-[4rem] bg-white font-inter relative overflow-hidden">
        <div className="mx-[7%] md:mx-[7%] xl:mx-[50px] ipad-pro:mx-[50px] shadow-custom-lg rounded-[20px] bg-white">
          <div
            className="w-full text-center px-[3%] xl:px-[30px] ipad-pro:px-[30px] pt-[3rem]"
            data-aos="fade-up"
          >
            <h2 className="font-inter font-bold text-[1.5625rem] xl:text-[2.1875rem] ipad-pro:text-[1.875rem] md:text-[2.1875rem] leading-[2.45rem] text-[#061C3D]">
              Our Solution
            </h2>
            {project.solutionIntro && (
              <p className="font-inter font-normal text-[0.9375rem] xl:text-[1.15rem] ipad-pro:text-[1.125rem] md:text-[1.15rem] leading-[1.5875rem] pt-[30px] text-gray-700">
                {project.solutionIntro}
              </p>
            )}
          </div>

          <div className="relative px-[3%] xl:px-[30px] ipad-pro:px-[30px] pb-[3rem] pt-[3rem]">
            {solutionLines.length > 1 ? (
              <div
                className="flex flex-wrap justify-center gap-[1.5rem]"
                data-aos="fade-up"
              >
                {solutionLines.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="relative group bg-white border border-gray-200 rounded-[16px] p-6 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden xl:w-[calc(50%-0.75rem)] ipad-pro:w-[calc(50%-0.75rem)] w-full"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 min-w-[44px] rounded-full bg-gradient-to-br from-[#1887C9] to-[#0F5A8F] flex items-center justify-center shadow-lg flex-shrink-0">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-5 h-5 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={3}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      <div>
                        {item.title && (
                          <h3 className="text-[#061C3D] font-bold text-[1rem] mb-1">
                            {item.title}
                          </h3>
                        )}
                        <p className="text-gray-700 text-[0.9375rem] xl:text-[1rem] leading-[1.6rem]">
                          {item.body}
                        </p>
                      </div>
                    </div>
                    {/* Hover top bar */}
                    <div className="absolute top-0 left-0 w-0 group-hover:w-full h-[3px] bg-[#1887C9] transition-all duration-500" />
                  </motion.div>
                ))}
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <p className="text-gray-700 text-[0.9375rem] xl:text-[1.15rem] leading-[1.9rem]">
                  {project.solution}
                </p>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════
                RESULTS & IMPACT
            ════════════════════════════════════ */}
   {project?.resultsImpact?.length > 0 && (
  <div className="py-[4rem] bg-[#061C3D] font-inter">
    <div className="mx-[7%] md:mx-[7%] xl:mx-[50px] ipad-pro:mx-[50px]">
      <div className="flex flex-col overflow-hidden px-[3%] xl:px-[30px] ipad-pro:px-[30px] shadow-[0px_0px_3px_#707070] rounded-[20px] border-black border-[0.0001px] p-12">

        {/* Heading */}
        {(project?.resultsIntro) && (
          <div className="mb-8 text-white" data-aos="fade-up">
            <h2 className="text-[1.5625rem] xl:text-[2.1875rem] md:text-[2.1875rem] font-bold mb-4">
              Results & Impact
            </h2>

            {project.resultsIntro && (
              <p className="text-white/80">
                {project.resultsIntro}
              </p>
            )}
          </div>
        )}

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {project.resultsImpact.map((item, index) => {
            const isOdd = project.resultsImpact.length % 2 !== 0;
            const isLast = index === project.resultsImpact.length - 1;

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`bg-white/10 backdrop-blur-md border border-white/20 rounded-[16px] p-6 hover:bg-white/20 transition-all duration-300
                  ${isOdd && isLast ? "md:col-span-2 md:max-w-[50%] md:mx-auto" : ""}
                `}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 min-w-[40px] rounded-full bg-gradient-to-br from-[#1887C9] to-[#0F5A8F] flex items-center justify-center shadow-md">
                    ✓
                  </div>

                  <div>
                    {item?.title && (
                      <h3 className="text-white font-bold mb-1">
                        {item.title}
                      </h3>
                    )}
                    {item?.body && (
                      <p className="text-white/70">
                        {item.body}
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

      </div>
    </div>
  </div>
)}
{/* ════════════════════════════════════
    KEY PLATFORM FEATURES
════════════════════════════════════ */}
{project.platformFeatures && project.platformFeatures.length > 0 && (
  <div className="py-[4rem] bg-[#EBEDEE] font-inter relative overflow-hidden">
    <div className="mx-[7%] md:mx-[7%] xl:mx-[50px] ipad-pro:mx-[50px] shadow-custom-lg rounded-[20px] bg-white">
      <div
        className="w-full text-center px-[3%] xl:px-[30px] ipad-pro:px-[30px] pt-[3rem]"
        data-aos="fade-up"
      >
        <h2 className="font-inter font-bold text-[1.5625rem] xl:text-[2.1875rem] ipad-pro:text-[1.875rem] md:text-[2.1875rem] leading-[2.45rem] text-[#061C3D]">
          Key Platform Features
        </h2>
        {project.platformFeaturesIntro && (
          <p className="font-inter font-normal text-[0.9375rem] xl:text-[1.15rem] ipad-pro:text-[1.125rem] md:text-[1.15rem] leading-[1.5875rem] pt-[30px] text-gray-700">
            {project.platformFeaturesIntro}
          </p>
        )}
      </div>

      <div className="relative px-[3%] xl:px-[30px] ipad-pro:px-[30px] pb-[3rem] pt-[3rem]">
        <div
          className="flex flex-wrap justify-center gap-[1.5rem]"
          data-aos="fade-up"
        >
          {project.platformFeatures.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="relative group bg-white border border-gray-200 rounded-[16px] p-6 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden xl:w-[calc(50%-0.75rem)] ipad-pro:w-[calc(50%-0.75rem)] w-full"
            >
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 min-w-[44px] rounded-full bg-gradient-to-br from-[#1887C9] to-[#0F5A8F] flex items-center justify-center shadow-lg flex-shrink-0">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-5 h-5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-[#061C3D] font-bold text-[1rem] mb-1">
                    {feature.title}
                  </h3>
                  <p className="text-gray-700 text-[0.9375rem] xl:text-[1rem] leading-[1.6rem]">
                    {feature.body}
                  </p>
                </div>
              </div>
              {/* Hover top bar */}
              <div className="absolute top-0 left-0 w-0 group-hover:w-full h-[3px] bg-[#1887C9] transition-all duration-500" />
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </div>
)}

{/* ════════════════════════════════════
    BEYOND THE METRICS
════════════════════════════════════ */}
{project.beyondMetrics && project.beyondMetrics.length > 0 && (
  <div className="py-[4rem] bg-[#061C3D] font-inter">
    <div className="mx-[7%] md:mx-[7%] xl:mx-[50px] ipad-pro:mx-[50px]">
      <div className="flex flex-col overflow-hidden px-[3%] xl:px-[30px] ipad-pro:px-[30px] shadow-[0px_0px_3px_#707070] rounded-[20px] border-black border-[0.0001px] p-12">
        <div className="mb-8 text-white" data-aos="fade-up">
          <h2 className="text-[1.5625rem] xl:text-[2.1875rem] ipad-pro:text-[1.875rem] md:text-[2.1875rem] leading-[2.45rem] font-bold mb-4">
            Beyond the Metrics
          </h2>
          {project.beyondMetricsIntro && (
            <p className="text-[0.9375rem] xl:text-[1.15rem] ipad-pro:text-[1.125rem] md:text-[1.15rem] leading-[1.5875rem] text-white/80">
              {project.beyondMetricsIntro}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {project.beyondMetrics.map((item, index) => {
            const isOdd = project.beyondMetrics.length % 2 !== 0;
            const isLast = index === project.beyondMetrics.length - 1;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`bg-white/10 backdrop-blur-md border border-white/20 rounded-[16px] p-6 hover:bg-white/20 transition-all duration-300
                  ${isOdd && isLast ? "md:col-span-2 md:max-w-[50%] md:mx-auto" : ""}
                `}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 min-w-[40px] rounded-full bg-gradient-to-br from-[#1887C9] to-[#0F5A8F] flex items-center justify-center shadow-md flex-shrink-0">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-4 h-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                  </div>
                  <div>
                    {item.title && (
                      <h3 className="text-white font-bold text-[1rem] mb-1">
                        {item.title}
                      </h3>
                    )}
                    <p className="text-white/70 text-[0.9375rem] xl:text-[1rem] leading-[1.6rem]">
                      {item.body}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  </div>
)}
      {/* ════════════════════════════════════
                TECHNOLOGY STACK
            ════════════════════════════════════ */}
      {project.techStacks && project.techStacks.length > 0 && (
        <div className="py-[4rem] bg-white font-inter relative overflow-hidden">
          <div className="mx-[7%] md:mx-[7%] xl:mx-[50px] ipad-pro:mx-[50px] shadow-custom-lg rounded-[20px] bg-white">
            <div
              className="w-full text-center px-[3%] xl:px-[30px] ipad-pro:px-[30px] pt-[3rem]"
              data-aos="fade-up"
            >
              <h2 className="font-inter font-bold text-[1.5625rem] xl:text-[2.1875rem] ipad-pro:text-[1.875rem] md:text-[2.1875rem] leading-[2.45rem] text-[#061C3D]">
                Technology Stack
              </h2>
              <p className="font-inter font-normal text-[0.9375rem] xl:text-[1.15rem] ipad-pro:text-[1.125rem] md:text-[1.15rem] leading-[1.5875rem] pt-[30px] text-gray-700">
                The carefully selected technologies that power this platform.
              </p>
            </div>

            <div className="relative px-[3%] xl:px-[30px] ipad-pro:px-[30px] pb-[3rem] pt-[3rem]">
              <div
                className="flex flex-wrap justify-center gap-[1.5rem]"
                data-aos="fade-up"
              >
                {project.techStacks.map((tech, index) => {
                  // NEW — uses project-specific categories, falls back to global map
                  const category =
                    (project.techStackCategories &&
                      project.techStackCategories[tech]) ||
                    "Other";
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.35, delay: index * 0.07 }}
                      viewport={{ once: true }}
                      className="flex flex-col items-center justify-center p-5 bg-white rounded-[16px] shadow-md hover:shadow-xl border border-gray-200 hover:border-[#1887C9] transition-all duration-300 group text-center gap-2 w-[calc(50%-0.75rem)] sm:w-[calc(33.33%-1rem)] md:w-[calc(25%-1.125rem)] xl:w-[calc(20%-1.2rem)]"
                    >
                      <p className="font-bold text-[#061C3D] text-[0.9375rem] xl:text-[1rem] leading-snug">
                        {tech}
                      </p>
                      <span className="text-[0.75rem] text-[#1887C9] font-semibold bg-[#1887C9] bg-opacity-10 px-3 py-0.5 rounded-full">
                        {category}
                      </span>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════
                CTA
            ════════════════════════════════════ */}
      <div className="py-[4rem] bg-[#061C3D] font-inter">
        <div className="mx-[7%] md:mx-[7%] xl:mx-[50px] ipad-pro:mx-[50px]">
          <div
            className="relative overflow-hidden rounded-[20px] bg-white/10 border border-white/15 px-[5%] xl:px-[60px] ipad-pro:px-[40px] py-[3rem] flex flex-col xl:flex-row ipad-pro:flex-row items-center justify-between gap-8 shadow-[0px_0px_3px_#707070]"
            data-aos="fade-up"
          >
            {/* Decorative blur */}
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#1887C9] opacity-10 blur-3xl pointer-events-none" />

            <div className="xl:w-[70%] ipad-pro:w-[60%] text-center xl:text-left ipad-pro:text-left">
              <span className="text-[1.5625rem] xl:text-[2rem] ipad-pro:text-[1.875rem] font-bold font-inter text-white leading-[2.45rem]">
                 {project.consultationIntro}
              </span>
            </div>

            <div className="flex flex-col sm:flex-row xl:flex-col ipad-pro:flex-col gap-4 items-center">
              <a
                href="/contact"
                className="inline-flex items-center justify-center gap-2 px-7 py-3 bg-[#1887C9] hover:bg-[#0F5A8F] text-white font-bold font-inter rounded-full text-[1rem] transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Get a Free Consultation
              </a>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default CaseStudyPages;